import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    X, Copy, RefreshCw, Loader2, AlertTriangle, CheckCircle2,
    Trash2, ExternalLink, ShieldCheck, Pencil, Save, BookOpen,
    ChevronDown, ChevronRight,
} from "lucide-react";
import { broadcastStudyConfig } from "@/lib/useStudyConfig";

/**
 * <ProlificStudyModal /> — admin tool that diagnoses + fixes the
 * Prolific study link. Built after the Feb 2026 incident where every
 * Prolific participant saw "This study session has already been
 * completed" because the literal `{{%PROLIFIC_PID%}}` placeholder was
 * being treated as a real iid (the first un-substituted test landed,
 * completed the study, and suspended the placeholder account, which
 * then blocked every subsequent click).
 *
 * Three actions:
 *   • "Reopen suspended"  — un-suspends every completed Prolific
 *     account so participants can revisit. Also nukes any
 *     placeholder-string accounts as a hygiene step.
 *   • "Delete bogus only" — removes placeholder-string accounts
 *     without touching anything else.
 *   • "Wipe all Prolific testers" — destructive, last resort. Hidden
 *     behind an extra confirmation step + the x-confirm-delete header.
 */
export default function ProlificStudyModal({ open, onClose }) {
    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(false);
    const [acting, setActing] = useState(null);
    const [wipeArmed, setWipeArmed] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [edit, setEdit] = useState({ completion_code: "", screen_out_code: "", study_name: "" });
    const [savingConfig, setSavingConfig] = useState(false);
    // "How to set up a new study" playbook — collapsed by default so it
    // doesn't crowd the workflow, but one click away for next time.
    // Sticky-collapsed state persists across reopens.
    const [helpOpen, setHelpOpen] = useState(() => {
        if (typeof window === "undefined") return false;
        try { return window.localStorage.getItem("ml_prolific_help_open") === "1"; }
        catch { return false; }
    });
    useEffect(() => {
        if (typeof window === "undefined") return;
        try { window.localStorage.setItem("ml_prolific_help_open", helpOpen ? "1" : "0"); }
        catch { /* private mode */ }
    }, [helpOpen]);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/testers/prolific-health");
            setHealth(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load Prolific health.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) {
            setWipeArmed(false);
            setEditOpen(false);
            refresh();
        }
    }, [open, refresh]);

    // When the modal opens (or after refresh), seed the edit form with
    // whatever the backend currently has, so opening the editor never
    // shows stale or empty values.
    useEffect(() => {
        if (health) {
            setEdit({
                completion_code: health.completion_code || "",
                screen_out_code: health.screen_out_code || "",
                study_name: health.study_name || "",
            });
        }
    }, [health]);

    if (!open) return null;

    const reopen = async () => {
        setActing("reopen");
        try {
            const r = await api.post("/admin/testers/reset-prolific", { mode: "reopen" });
            toast.success(`Re-opened ${r.data.reopened} session${r.data.reopened === 1 ? "" : "s"} · removed ${r.data.deleted_bogus} bogus account${r.data.deleted_bogus === 1 ? "" : "s"}.`);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reopen failed.");
        } finally { setActing(null); }
    };

    const deleteBogus = async () => {
        setActing("delete_bogus");
        try {
            const r = await api.post("/admin/testers/reset-prolific", { mode: "delete_bogus" });
            toast.success(`Removed ${r.data.deleted} placeholder account${r.data.deleted === 1 ? "" : "s"}.`);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed.");
        } finally { setActing(null); }
    };

    const wipeAll = async () => {
        setActing("wipe");
        try {
            const r = await api.post(
                "/admin/testers/reset-prolific",
                { mode: "delete_all_prolific" },
                { headers: { "x-confirm-delete": "yes" } },
            );
            toast.success(`Deleted ${r.data.deleted} Prolific tester account${r.data.deleted === 1 ? "" : "s"}.`);
            setWipeArmed(false);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Wipe failed.");
        } finally { setActing(null); }
    };

    const copyUrl = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Copied to clipboard.");
        } catch {
            toast.error("Clipboard unavailable — copy manually.");
        }
    };

    /**
     * "Test the link" — opens /tester/?PROLIFIC_PID=admin-test-<ts>
     * in a new tab so the founder can walk the full participant flow
     * without going through Prolific's dashboard and without burning a
     * real participant slot. The "admin-test-" prefix is deliberate:
     *   • lets us spot test sessions in the diagnostic list,
     *   • makes them safe to wipe in bulk via the standard
     *     reset/wipe actions (they get filtered + cleaned by the
     *     existing "Wipe all Prolific testers" path).
     * No backend changes needed — the existing tester provisioning
     * accepts any non-placeholder PID.
     */
    const openTestLink = () => {
        const ts = Date.now().toString(36);
        const testPid = `admin-test-${ts}`;
        const url = `/tester/?PROLIFIC_PID=${encodeURIComponent(testPid)}` +
            `&STUDY_ID=admin-test-study&SESSION_ID=admin-test-session`;
        window.open(url, "_blank", "noopener,noreferrer");
        toast.success(
            `Throwaway tester provisioned with PID ${testPid}. ` +
            "Clean up later via Reopen or Wipe all.",
            { duration: 6000 },
        );
        // Refresh so the new test account shows in the diagnostic list.
        setTimeout(refresh, 1500);
    };

    /**
     * Persist edits to /api/admin/study-config. Server validates the
     * codes (alphanumeric, ≤32 chars) so a typo can't brick the
     * completion redirect. On success we also push the new value into
     * the localStorage-backed cache via broadcastStudyConfig so other
     * open tabs (especially the public /study-complete + /screener)
     * pick up the change without a refresh.
     */
    const saveConfig = async () => {
        setSavingConfig(true);
        try {
            const r = await api.post("/admin/study-config", {
                completion_code: edit.completion_code.trim().toUpperCase(),
                screen_out_code: edit.screen_out_code.trim().toUpperCase(),
                study_name: edit.study_name.trim(),
            });
            broadcastStudyConfig(r.data);
            toast.success("Study config saved. Cross-tab caches refreshed.");
            setEditOpen(false);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save study config.");
        } finally {
            setSavingConfig(false);
        }
    };

    const t = health?.totals || {};

    return (
        <div
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="prolific-modal"
        >
            <div
                className="ml-card p-0 max-w-2xl w-full max-h-[90vh] bg-white overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-4 border-b border-cream-dark">
                    <div className="flex items-start gap-3">
                        <span className="w-10 h-10 rounded-2xl bg-teal-soft text-teal-dark flex items-center justify-center shrink-0">
                            <ShieldCheck size={18} strokeWidth={1.8} />
                        </span>
                        <div>
                            <h2 className="font-heading text-xl text-[#1F2624]">Prolific Study Health</h2>
                            <p className="text-sm text-[#4C5551] leading-relaxed max-w-md">
                                Diagnose + reset the per-participant link. New testers can come through as soon as suspended sessions are re-opened.
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} aria-label="Close" data-testid="prolific-modal-close"
                        className="p-2 -m-2 text-[#6E7A74] hover:text-[#4C5551]">
                        <X size={18} strokeWidth={1.8} />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5 overflow-y-auto space-y-5">
                    {loading && !health ? (
                        <div className="text-center text-sm text-[#4C5551] flex items-center justify-center gap-2 py-8">
                            <Loader2 size={14} className="animate-spin" /> Loading study health…
                        </div>
                    ) : (
                        <>
                            {/* Totals */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="prolific-totals">
                                <StatTile label="All Prolific" value={t.all_prolific_testers ?? 0} />
                                <StatTile label="Active" value={t.active_prolific_testers ?? 0} tone="green" />
                                <StatTile label="Completed / blocked" value={t.completed_or_suspended ?? 0} tone="amber" />
                                <StatTile label="Bogus placeholder" value={t.misconfigured_placeholder_accounts ?? 0} tone={(t.misconfigured_placeholder_accounts ?? 0) > 0 ? "coral" : "neutral"} />
                            </div>

                            {/* Help drawer — collapsible playbook for setting up
                                a new (or duplicated) Prolific study. Collapsed
                                by default so it doesn't crowd the diagnostic
                                view; sticky open/closed state per founder. */}
                            <ProlificHelpDrawer open={helpOpen} onToggle={() => setHelpOpen((v) => !v)} />

                            {/* Live Prolific codes (editable inline) */}
                            <div className="ml-card p-3" data-testid="prolific-config-card">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1">
                                        <p className="text-[10px] uppercase tracking-wider font-semibold text-[#4C5551]">
                                            Active study
                                        </p>
                                        <p className="text-sm text-[#1F2624] font-medium mt-0.5" data-testid="prolific-active-study-name">
                                            {health?.study_name || "—"}
                                        </p>
                                        <p className="text-[11px] text-[#4C5551] mt-1">
                                            <span className="font-mono text-teal-dark">{health?.completion_code || "—"}</span>
                                            <span className="text-[#6E7A74]"> · completion · </span>
                                            <span className="font-mono text-stage_late-dark">{health?.screen_out_code || "—"}</span>
                                            <span className="text-[#6E7A74]"> · screen-out</span>
                                        </p>
                                        {health?.config_updated_at && (
                                            <p className="text-[10px] text-[#6E7A74] mt-1">
                                                Updated {new Date(health.config_updated_at).toLocaleString()}
                                            </p>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setEditOpen((v) => !v)}
                                        data-testid="prolific-edit-config-toggle"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-2xl text-xs font-medium border border-teal-soft bg-white text-teal-dark hover:bg-teal-soft shrink-0"
                                    >
                                        <Pencil size={12} strokeWidth={2} />
                                        {editOpen ? "Cancel" : "Edit"}
                                    </button>
                                </div>

                                {editOpen && (
                                    <div className="mt-3 pt-3 border-t border-cream-dark space-y-3" data-testid="prolific-edit-config-form">
                                        {/* Paste-the-URL helper — most of the friction
                                            is finding the right field in Prolific's UI.
                                            If you paste the Redirect URL straight from
                                            Prolific (e.g. "https://app.prolific.com/
                                            submissions/complete?cc=C1PAR2IC"), we
                                            extract the cc=XXX automatically and drop
                                            it into the Completion code field. */}
                                        <PasteUrlHelper
                                            onCode={(code, kind) => setEdit((s) => ({
                                                ...s,
                                                [kind === "screen_out" ? "screen_out_code" : "completion_code"]: code,
                                            }))}
                                        />
                                        <ConfigField
                                            label="Study name"
                                            value={edit.study_name}
                                            onChange={(v) => setEdit((s) => ({ ...s, study_name: v }))}
                                            placeholder="e.g. Memory Loss and Dementia Care Navigation"
                                            testid="prolific-edit-study-name"
                                        />
                                        <ConfigField
                                            label="Completion code (cc)"
                                            value={edit.completion_code}
                                            onChange={(v) => setEdit((s) => ({ ...s, completion_code: v.toUpperCase() }))}
                                            placeholder="e.g. C1PAR2IC"
                                            mono
                                            help="Sent to participants who finish the study. Find this in your Prolific study's Completion section."
                                            testid="prolific-edit-completion-code"
                                        />
                                        <ConfigField
                                            label="Screen-out code (cc)"
                                            value={edit.screen_out_code}
                                            onChange={(v) => setEdit((s) => ({ ...s, screen_out_code: v.toUpperCase() }))}
                                            placeholder="e.g. C1HDHBIO"
                                            mono
                                            help="Sent to participants who fail the pre-screener. Usually a separate code in Prolific."
                                            testid="prolific-edit-screen-out-code"
                                        />
                                        <div className="flex items-center justify-end gap-2 pt-1">
                                            <button
                                                type="button"
                                                onClick={() => setEditOpen(false)}
                                                disabled={savingConfig}
                                                className="px-3 py-1.5 rounded-2xl border border-cream-dark text-xs font-medium text-[#4C5551] hover:bg-cream-dark/30"
                                                data-testid="prolific-edit-cancel"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                onClick={saveConfig}
                                                disabled={savingConfig}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-2xl bg-teal text-white text-xs font-medium hover:bg-teal-dark disabled:opacity-60"
                                                data-testid="prolific-edit-save"
                                            >
                                                {savingConfig ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} strokeWidth={2} />}
                                                Save
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Recommended URL */}
                            <div className="ml-card p-3 bg-cream-light/50" data-testid="prolific-recommended-url">
                                <p className="text-[10px] uppercase tracking-wider font-semibold text-[#4C5551] mb-1">
                                    Recommended study URL (paste into Prolific)
                                </p>
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 text-[11px] font-mono text-[#1F2624] break-all bg-white p-2 rounded-md border border-cream-dark">
                                        {health?.study_url_recommended || "—"}
                                    </code>
                                    <button
                                        onClick={() => copyUrl(health?.study_url_recommended)}
                                        className="p-2 rounded-md text-teal-dark hover:bg-teal-soft"
                                        title="Copy URL"
                                        data-testid="prolific-copy-url"
                                    >
                                        <Copy size={14} strokeWidth={2} />
                                    </button>
                                </div>
                                <p className="text-[11px] text-[#6E7A74] mt-1.5">
                                    Prolific substitutes the <code>{`{{%PROLIFIC_PID%}}`}</code> tokens at click time so each participant gets a unique session.
                                </p>
                                <div className="mt-3 p-2.5 bg-yellow-soft/60 rounded-lg border border-yellow-soft">
                                    <p className="text-[11px] font-semibold text-yellow-dark mb-1">
                                        Clicking the URL above directly won&apos;t work — that&apos;s expected.
                                    </p>
                                    <p className="text-[11px] text-yellow-dark/90 leading-relaxed mb-2">
                                        Prolific only swaps in a real <code>PROLIFIC_PID</code> when a participant clicks through their Prolific dashboard. To test the flow yourself, use the throwaway link below — it provisions a one-off tester account so you can walk the whole experience without burning a real participant slot.
                                    </p>
                                    <button
                                        onClick={openTestLink}
                                        data-testid="prolific-test-link"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-2xl bg-teal text-white text-xs font-medium hover:bg-teal-dark"
                                    >
                                        <ExternalLink size={12} strokeWidth={2} /> Test the link with a throwaway PID
                                    </button>
                                </div>
                            </div>

                            {/* Bogus warning */}
                            {(t.misconfigured_placeholder_accounts ?? 0) > 0 && (
                                <div className="ml-card p-3 bg-coral-light/40 border-coral-light" data-testid="prolific-bogus-alert">
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle size={16} strokeWidth={2} className="text-coral-dark shrink-0 mt-0.5" />
                                        <div className="text-xs text-coral-dark leading-relaxed">
                                            <strong>{t.misconfigured_placeholder_accounts} placeholder account{t.misconfigured_placeholder_accounts === 1 ? "" : "s"}</strong> {t.misconfigured_placeholder_accounts === 1 ? "was" : "were"} created from an un-substituted Prolific URL.
                                            These were almost certainly the cause of the &ldquo;study completed&rdquo; message every participant was seeing — the server now rejects placeholder strings, but you should clean up the existing records.
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Action buttons */}
                            <div className="space-y-2" data-testid="prolific-actions">
                                <ActionRow
                                    title="Re-open suspended sessions"
                                    desc="Un-suspends every completed Prolific tester and removes placeholder accounts. Their feedback + activity data stays on the user record. Safe to run repeatedly."
                                    cta="Re-open + clean placeholders"
                                    busy={acting === "reopen"}
                                    disabled={acting !== null}
                                    onClick={reopen}
                                    testid="prolific-reopen"
                                    Icon={RefreshCw}
                                />
                                <ActionRow
                                    title="Delete only placeholder accounts"
                                    desc="Removes the bogus iid=PROLIFIC_PID rows without touching real participants. Use this if you don't want to re-open completed sessions."
                                    cta="Delete bogus only"
                                    busy={acting === "delete_bogus"}
                                    disabled={acting !== null || (t.misconfigured_placeholder_accounts ?? 0) === 0}
                                    onClick={deleteBogus}
                                    testid="prolific-delete-bogus"
                                    Icon={Trash2}
                                />
                                <ActionRow
                                    title="Wipe ALL Prolific testers"
                                    desc="Destructive — deletes every Prolific tester account, completed or active. Use only if you want a totally clean slate. Requires confirmation."
                                    cta={wipeArmed ? "Yes, wipe everything" : "Arm wipe"}
                                    busy={acting === "wipe"}
                                    disabled={acting !== null || (t.all_prolific_testers ?? 0) === 0}
                                    onClick={() => (wipeArmed ? wipeAll() : setWipeArmed(true))}
                                    testid={wipeArmed ? "prolific-wipe-confirm" : "prolific-wipe-arm"}
                                    Icon={Trash2}
                                    tone="coral"
                                />
                            </div>

                            {/* Lists */}
                            {(health?.bogus_iids?.length || 0) > 0 && (
                                <DiagnosticList
                                    title={`Bogus placeholder accounts (${health.bogus_iids.length})`}
                                    rows={health.bogus_iids}
                                    cols={["participant_id", "status", "created_at"]}
                                    testid="prolific-bogus-list"
                                />
                            )}
                            {(health?.recent_suspended?.length || 0) > 0 && (
                                <DiagnosticList
                                    title="Most-recently suspended Prolific testers"
                                    rows={health.recent_suspended}
                                    cols={["participant_id", "tester_completed_at", "suspended_reason"]}
                                    testid="prolific-recent-list"
                                />
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-2 px-6 py-3 border-t border-cream-dark bg-cream-light/40">
                    <a
                        href={health?.completion_url || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-teal-dark hover:underline"
                        data-testid="prolific-completion-url"
                    >
                        Completion URL <ExternalLink size={12} strokeWidth={2} />
                    </a>
                    <button onClick={refresh} disabled={loading}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-[#4C5551] hover:text-teal-dark"
                        data-testid="prolific-refresh"
                    >
                        {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} strokeWidth={2} />}
                        Refresh
                    </button>
                </div>
            </div>
        </div>
    );
}

function StatTile({ label, value, tone = "neutral" }) {
    const palette = ({
        green: "bg-stage_early/20 text-stage_early-dark",
        amber: "bg-yellow-soft text-yellow-dark",
        coral: "bg-coral-light/50 text-coral-dark",
        neutral: "bg-cream-light text-[#4C5551]",
    })[tone];
    return (
        <div className={`rounded-xl px-3 py-2 ${palette}`}>
            <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
            <div className="text-lg font-heading font-semibold mt-0.5">{value}</div>
        </div>
    );
}

function ActionRow({ title, desc, cta, busy, disabled, onClick, testid, Icon, tone = "teal" }) {
    const btnPalette = tone === "coral"
        ? "bg-coral-light/40 text-coral-dark hover:bg-coral-light/70 border-coral-light"
        : "bg-teal-soft text-teal-dark hover:bg-teal-light border-teal-soft";
    return (
        <div className="ml-card p-3 flex items-start gap-3" data-testid={`${testid}-row`}>
            <div className="flex-1">
                <p className="font-medium text-sm text-[#1F2624]">{title}</p>
                <p className="text-xs text-[#4C5551] leading-relaxed mt-0.5">{desc}</p>
            </div>
            <button
                onClick={onClick}
                disabled={disabled}
                data-testid={testid}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-2xl border text-xs font-medium shrink-0 disabled:opacity-40 disabled:cursor-not-allowed ${btnPalette}`}
            >
                {busy ? <Loader2 size={12} className="animate-spin" /> : Icon ? <Icon size={12} strokeWidth={2} /> : <CheckCircle2 size={12} strokeWidth={2} />}
                {cta}
            </button>
        </div>
    );
}

function ProlificHelpDrawer({ open, onToggle }) {
    return (
        <div
            className="rounded-2xl border border-cream-dark overflow-hidden"
            data-testid="prolific-help-drawer"
        >
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                data-testid="prolific-help-toggle"
                className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left bg-cream-light/60 hover:bg-cream-light"
            >
                <span className="inline-flex items-center gap-2">
                    <BookOpen size={15} strokeWidth={2} className="text-teal-dark" />
                    <span className="font-medium text-sm text-[#1F2624]">
                        How to set up a new (or duplicated) Prolific study
                    </span>
                </span>
                {open
                    ? <ChevronDown size={15} strokeWidth={2} className="text-[#4C5551]" />
                    : <ChevronRight size={15} strokeWidth={2} className="text-[#4C5551]" />}
            </button>

            {open && (
                <div className="px-4 py-4 bg-white text-xs text-[#4C5551] leading-relaxed space-y-4" data-testid="prolific-help-body">

                    <Step
                        n="1"
                        title="In Prolific (one-time per new study)"
                        items={[
                            "Open your new study → scroll to Study completion.",
                            <>Set <b>Redirect URL</b> to <code className="font-mono text-teal-dark">https://memorylanecare.net/study-complete</code> (or keep Prolific&apos;s default — both work).</>,
                            <>Copy the <b>Completion code</b> (e.g. <code className="font-mono">C1O8667A</code>).</>,
                            "If you have a screener, also copy the Screen-out code.",
                            <>Set the <b>Study URL</b> (where Prolific sends participants) to exactly:
                                <code className="block font-mono text-[10px] mt-1 p-2 bg-cream-light rounded-md text-[#1F2624] break-all">
                                    https://memorylanecare.net/tester/?PROLIFIC_PID={`{{`}%PROLIFIC_PID%{`}}`}&STUDY_ID={`{{`}%STUDY_ID%{`}}`}&SESSION_ID={`{{`}%SESSION_ID%{`}}`}
                                </code>
                                <span className="text-[#6E7A74]">Plain <code>&amp;</code>, never <code>&amp;amp;</code>. Same URL every study — never changes.</span>
                            </>,
                        ]}
                    />

                    <Step
                        n="2"
                        title="In Memory Lane Admin (10 seconds)"
                        items={[
                            "You're already here. Below this drawer, click Edit on the Active study card.",
                            <>Update <b>Study name</b> to match what you named it in Prolific.</>,
                            <>In the <b>Paste from Prolific</b> field, paste either the full Completion URL OR just the code itself, choose <b>→ Completion</b> from the dropdown, click <b>Use</b>.</>,
                            <>If you have a screen-out code: paste it, select <b>→ Screen-out</b>, click <b>Use</b>.</>,
                            <>Click <b>Save</b>. Toast confirms. Live everywhere — no redeploy.</>,
                        ]}
                    />

                    <Step
                        n="3"
                        title="Verify (30 seconds)"
                        items={[
                            <>In this modal, click <b>&quot;Test the link with a throwaway PID&quot;</b> → opens a fake tester session.</>,
                            "Walk through enough of the tour to find the Feedback survey.",
                            "Submit feedback. You land on /study-complete with the new code shown.",
                            "Click \"Return to Prolific\" — the redirect URL ends with your new cc=XXXXXXXX.",
                            <>Back here, click <b>&quot;Re-open + clean placeholders&quot;</b> to scrub the throwaway test account.</>,
                        ]}
                    />

                    <Step
                        n="·"
                        title="Topping up an existing study (recommended — zero touch)"
                        items={[
                            "In Prolific → your study → Add submissions / Republish — pay for more slots. Same code, nothing changes in Memory Lane.",
                            "If \"top up failed\" appears, fix it as a Prolific billing problem first (payment method, account balance, study limits) before duplicating. Duplicating is what creates new codes.",
                        ]}
                    />

                    <div className="rounded-xl bg-cream-light/70 p-3 mt-2" data-testid="prolific-help-troubleshoot">
                        <p className="font-semibold text-[#1F2624] text-xs mb-2">When something goes wrong</p>
                        <table className="w-full text-[11px]">
                            <tbody>
                                <tr className="border-b border-cream-dark/60">
                                    <td className="py-1 pr-2"><b>&quot;UNKNOWN CODE&quot;</b> on Prolific</td>
                                    <td className="py-1 text-[#4C5551]">Wrong code saved here. Repeat Step 2 with the right code.</td>
                                </tr>
                                <tr className="border-b border-cream-dark/60">
                                    <td className="py-1 pr-2"><b>&quot;Study has been completed&quot;</b></td>
                                    <td className="py-1 text-[#4C5551]">Click <b>Re-open + clean placeholders</b> below.</td>
                                </tr>
                                <tr className="border-b border-cream-dark/60">
                                    <td className="py-1 pr-2"><b>Feedback missing from dashboard</b></td>
                                    <td className="py-1 text-[#4C5551]">Recoverable rows are at <code className="font-mono">GET /api/admin/prolific/failures</code>.</td>
                                </tr>
                                <tr>
                                    <td className="py-1 pr-2"><b>One specific participant issue</b></td>
                                    <td className="py-1 text-[#4C5551]"><code className="font-mono">/api/admin/prolific/diagnose-participant?pid=XXX</code> for the full timeline.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

function Step({ n, title, items }) {
    return (
        <div className="flex gap-3" data-testid={`prolific-help-step-${n}`}>
            <span className="shrink-0 w-6 h-6 rounded-full bg-teal-soft text-teal-dark text-[11px] font-semibold flex items-center justify-center">
                {n}
            </span>
            <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#1F2624] text-xs mb-1">{title}</p>
                <ol className="list-decimal pl-4 space-y-1.5 marker:text-[#6E7A74]">
                    {items.map((it, i) => <li key={i}>{it}</li>)}
                </ol>
            </div>
        </div>
    );
}

function PasteUrlHelper({ onCode }) {
    const [url, setUrl] = React.useState("");
    const [parsed, setParsed] = React.useState(null);
    const [kind, setKind] = React.useState("completion");

    // Tries to pull `cc=XXXXXXXX` out of whatever the founder pastes:
    //   • Full Prolific URL  ➜ extracts `cc` param.
    //   • Raw code alone     ➜ accepted as-is if alphanumeric.
    //   • Anything else      ➜ silently does nothing (no scary error).
    React.useEffect(() => {
        const raw = (url || "").trim();
        if (!raw) { setParsed(null); return; }
        try {
            // Try URL parse first
            const u = new URL(raw);
            const cc = u.searchParams.get("cc");
            if (cc && /^[A-Za-z0-9_-]{4,32}$/.test(cc)) {
                setParsed(cc.toUpperCase()); return;
            }
        } catch { /* not a URL, fall through */ }
        // Raw code path — only accept clearly-Prolific-shaped tokens
        if (/^[A-Za-z0-9_-]{6,32}$/.test(raw)) {
            setParsed(raw.toUpperCase()); return;
        }
        setParsed(null);
    }, [url]);

    const apply = () => {
        if (!parsed) return;
        onCode(parsed, kind);
        setUrl(""); setParsed(null);
        toast.success(`${kind === "screen_out" ? "Screen-out" : "Completion"} code set to ${parsed}.`);
    };

    return (
        <div className="rounded-2xl bg-cream-light/60 border border-cream-dark p-3" data-testid="prolific-paste-helper">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-[#4C5551] mb-1">
                Paste from Prolific
            </p>
            <p className="text-[11px] text-[#6E7A74] mb-2 leading-snug">
                Copy the &quot;Redirect URL&quot; or the &quot;Copy and paste code&quot; field from your
                Prolific study&apos;s completion panel and paste it here. We&apos;ll extract the
                code automatically.
            </p>
            <div className="flex gap-2 items-stretch">
                <input
                    type="text"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://app.prolific.com/submissions/complete?cc=C1O8667A — or just the code"
                    data-testid="prolific-paste-url-input"
                    className="flex-1 px-3 py-2 rounded-xl bg-white text-xs font-mono focus:outline-none focus:ring-2 focus:ring-teal"
                />
                <select
                    value={kind}
                    onChange={(e) => setKind(e.target.value)}
                    data-testid="prolific-paste-kind"
                    className="px-2 py-2 rounded-xl bg-white text-xs focus:outline-none focus:ring-2 focus:ring-teal"
                >
                    <option value="completion">→ Completion</option>
                    <option value="screen_out">→ Screen-out</option>
                </select>
                <button
                    type="button"
                    onClick={apply}
                    disabled={!parsed}
                    data-testid="prolific-paste-apply"
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-teal text-white text-xs font-medium hover:bg-teal-dark disabled:opacity-40"
                >
                    Use
                </button>
            </div>
            {parsed && (
                <p className="text-[11px] text-teal-dark mt-1.5" data-testid="prolific-paste-parsed">
                    Detected code:{" "}
                    <code className="font-mono font-semibold">{parsed}</code>
                </p>
            )}
        </div>
    );
}

function ConfigField({ label, value, onChange, placeholder, help, mono, testid }) {
    return (
        <label className="block">
            <span className="block text-[10px] uppercase tracking-wider font-semibold text-[#4C5551] mb-1">{label}</span>
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testid}
                className={`w-full px-3 py-2 rounded-xl bg-cream-light text-sm focus:outline-none focus:ring-2 focus:ring-teal ${mono ? "font-mono uppercase tracking-wide" : ""}`}
            />
            {help && <p className="text-[11px] text-[#6E7A74] mt-1">{help}</p>}
        </label>
    );
}

function DiagnosticList({ title, rows, cols, testid }) {
    if (!rows || rows.length === 0) return null;
    return (
        <details className="ml-card p-3 group" data-testid={testid}>
            <summary className="text-xs font-semibold text-[#1F2624] cursor-pointer">
                {title}
            </summary>
            <div className="mt-2 overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider font-semibold text-[#4C5551] border-b border-cream-dark">
                            {cols.map((c) => <th key={c} className="px-2 py-1">{c}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={r.id || i} className="border-b border-cream-dark/40">
                                {cols.map((c) => (
                                    <td key={c} className="px-2 py-1 font-mono text-[11px] text-[#4C5551]">
                                        {String(r[c] ?? "—")}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </details>
    );
}
